import { store } from '@restore/store';
import { DICOMViewerProcessesSlice } from '@restore/DICOMViewerProcesses/DICOMViewerProcesses.slice';
import { actions as qcTaskExecutionsActions } from '@restore/QCTaskExecutions/QCTaskExecutions.slice';
import { selectAuthState } from '@restore/auth/auth.slice';
import { EDTFSlice } from '@restore/EDTF/EDTF.slice';
import { segmentationSlice } from '@restore/segmentation/segmentation.slice';
import { DICOMViewerSlice } from '@restore/DICOMViewer/DICOMViewer.slice';
import { registerFormCall } from '../pendingCalls';
import { viewerRpcScheme } from './scheme';

/** Registers requests sent by the viewer and handled by D7 sagas. */
export const registerViewerHandlers = (): void => {
  const { process, dicomFile, edtf, dicomAudit, user, segmentation } =
    viewerRpcScheme.handlers;

  const createDispatchHandler =
    <T>(actionCreator: (payload: T) => { type: string }) =>
    (data: unknown) => {
      const { callId, promise } = registerFormCall();

      store.dispatch(
        actionCreator({
          ...((data ?? {}) as Record<string, unknown>),
          __callId: callId,
        } as T)
      );

      return promise;
    };

  /**
   * Answers "accepted", not "done": nothing behind these actions reports back.
   * A deferred here would strand an entry nothing settles, and the caller would
   * reject on the RPC timeout instead (D7-7889). Use `createDispatchHandler`
   * only where a saga settles the same `__callId`.
   */
  const createAckHandler =
    <T>(actionCreator: (payload: T) => { type: string }) =>
    async (data: unknown) => {
      store.dispatch(actionCreator((data ?? {}) as T));

      return { ok: true };
    };

  process.onRun(createDispatchHandler(DICOMViewerProcessesSlice.actions.runProcess));
  process.onList(createDispatchHandler(qcTaskExecutionsActions.fetchQCTaskExecutionsRequest));
  // The poll loop answers with a stream of PROCESSES_POLLED events, so the call
  // itself only acknowledges that polling started.
  process.onPoll(async () => {
    store.dispatch(qcTaskExecutionsActions.pollExecutions());

    return { ok: true };
  });
  dicomFile.onGet(createDispatchHandler(qcTaskExecutionsActions.downloadExportFile));
  edtf.onOpen(createAckHandler(EDTFSlice.actions.openEDTF));
  dicomAudit.onDownload(createAckHandler(DICOMViewerSlice.actions.downloadDICOMAudit));
  user.onGet(async () => selectAuthState(store.getState()).data ?? null);

  segmentation.onLock(createDispatchHandler(segmentationSlice.actions.lockMeasurement));
  segmentation.onUnlock(createDispatchHandler(segmentationSlice.actions.unlockMeasurement));
  segmentation.onDelete(createDispatchHandler(segmentationSlice.actions.deleteMeasurement));
};
